import { BrButton, BrDateTimePicker, BrSelect } from '@govbr-ds/react-components';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import { EFormaPagamento } from 'enums/EFormaPagamento';
import { TipoSituacaoGuiaDetalhe } from 'enums/ESituacaoGuia';
import { ETipoDeclaracao } from 'enums/ETipoDeclaracao';
import { ETipoRecolhimento } from 'enums/ETipoRecolhimento';
import { useNotificacao } from 'hooks/useNotificacao';
import { IMunicipio } from 'interfaces/IMunicipio';
import { ISubstancia } from 'interfaces/ISubstancia';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import { sfAnd, sfEqual, sfGt, sfLt, sfOr } from 'spring-filter-query-builder';
import { converterCompetenciaParaISO } from 'utils/formatCompetencia';
import { formatarNrProcesso } from 'utils/formatNrProcesso';
import { estadosUFOptions } from '../../../../utils/estadosUFLista';
import styles from './filtrosLista.module.scss';

interface IFiltrosGuiaRecolhimento {
  'processo.numero': string;
  'substanciaCorrelacionada.nome': string;
  'municipio.nome': string;
  'municipio.uf': string;
  tipoDeclarante: string;
  tipoRecolhimento: string;
  competencia: string;
  status: string;
  dataVencimento: string;
  dataVencimentoFinal: string;
  formaPagamento: string;
}

interface IFiltrosListaProps {
  setFiltrosGuiaRecolhimento: React.Dispatch<React.SetStateAction<string>>;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
}

const FiltrosLista = (props: IFiltrosListaProps) => {
  const MAX_DATE = new Date().toLocaleDateString('pt-BR');
  const FIRST_OPTION_LABEL = 'Selecionar';
  const DEFAULT_OPTION_VALUE = '-';

  const filtroInicial: IFiltrosGuiaRecolhimento = {
    'processo.numero': '',
    'substanciaCorrelacionada.nome': '',
    'municipio.nome': '',
    'municipio.uf': DEFAULT_OPTION_VALUE,
    tipoDeclarante: DEFAULT_OPTION_VALUE,
    tipoRecolhimento: DEFAULT_OPTION_VALUE,
    competencia: '',
    status: DEFAULT_OPTION_VALUE,
    dataVencimento: '',
    dataVencimentoFinal: '',
    formaPagamento: DEFAULT_OPTION_VALUE,
  };

  const [isFiltrosOpen, setIsFiltrosOpen] = useState(false);
  const [filtros, setFiltros] = useState<IFiltrosGuiaRecolhimento>(filtroInicial);
  const [estadoUF, setEstadoUF] = useState<string>(DEFAULT_OPTION_VALUE);
  const accordionHeaderStyle = isFiltrosOpen ? styles.open : '';

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');

  const { getMunicipiosPorUf, getProcessosMinerarios, getSubstancias } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();

  useEffect(() => {
    if (!estadoUF || estadoUF === DEFAULT_OPTION_VALUE) {
      onChangeFiltro('municipio.nome', DEFAULT_OPTION_VALUE);
    }
    onChangeFiltro('municipio.uf', estadoUF);
  }, [estadoUF]);

  const limparFiltros = () => {
    setFiltros(filtroInicial);
    setEstadoUF(DEFAULT_OPTION_VALUE);
    props.setFiltrosGuiaRecolhimento('');
    props.setPageNumber(1);
  };

  const removerFiltrosVazios = (filtros: IFiltrosGuiaRecolhimento) => {
    Object.keys(filtros).forEach((key) => {
      if (
        filtros[key as keyof IFiltrosGuiaRecolhimento] === '' ||
        filtros[key as keyof IFiltrosGuiaRecolhimento] == DEFAULT_OPTION_VALUE
      ) {
        delete filtros[key as keyof IFiltrosGuiaRecolhimento];
      }
    });
    return filtros;
  };

  const validarFiltros = (filtrosAplicados: IFiltrosGuiaRecolhimento) => {
    if (filtrosAplicados.dataVencimento && filtrosAplicados.dataVencimentoFinal) {
      const dataInicio = new Date(filtrosAplicados.dataVencimento);
      const dataFim = new Date(filtrosAplicados.dataVencimentoFinal);
      if (dataInicio > dataFim) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Data de vencimento inicial não pode ser maior que a data final.',
        });

        return false;
      }
    }

    return true;
  };

  const aplicarFiltros = () => {
    const filtrosAplicados = removerFiltrosVazios({ ...filtros });

    if (Object.keys(filtrosAplicados).length === 0) {
      limparFiltros();
      return;
    }

    if (!validarFiltros(filtrosAplicados)) {
      return;
    }
    const filtrosParaRequisicao = montarRequisicao(filtrosAplicados);
    props.setFiltrosGuiaRecolhimento(filtrosParaRequisicao.toString());
    props.setPageNumber(1);
  };

  const montarRequisicao = (filtros: IFiltrosGuiaRecolhimento) => {
    const itemFilters: string[] = [];
    Object.entries(filtros).forEach(([key, value]) => {
      if (value) {
        if (key === 'processo.numero') {
          const [numero, ano] = value.toString().replaceAll('.', '').split('/');
          itemFilters.push(sfEqual('processo.numero', numero).toString());
          itemFilters.push(sfEqual('processo.ano', ano).toString());
          return;
        }
        if (key === 'dataVencimento') {
          const [dia, mes, ano] = value.toString().split('/');
          const formattedDate = new Date(`${ano}-${mes}-${dia}`).toISOString().split('T')[0];
          itemFilters.push(sfGt('dataVencimento', formattedDate).toString());
          return;
        }
        if (key === 'dataVencimentoFinal') {
          const [dia, mes, ano] = value.toString().split('/');
          const formattedDate = new Date(`${ano}-${mes}-${dia}`).toISOString().split('T')[0];
          itemFilters.push(sfLt('dataVencimento', formattedDate).toString());
          return;
        }
        if (key === 'competencia') {
          const competenciaFormatada = converterCompetenciaParaISO(value);
          itemFilters.push(sfEqual('competencia', competenciaFormatada).toString());
          return;
        }
        if (key === 'status') {
          // Os 3 status serão considerados todos como VENCIDO
          if (value === TipoSituacaoGuiaDetalhe.Vencida.nome) {
            const situacaoGuiaVencida = sfOr([
              sfEqual('status', TipoSituacaoGuiaDetalhe.Cancelada.nome),
              sfEqual('status', TipoSituacaoGuiaDetalhe.Vencida.nome),
              sfEqual('status', TipoSituacaoGuiaDetalhe.Rejeitada.nome),
            ]).toString();
            itemFilters.push(situacaoGuiaVencida);
            return;
          }
        }
        itemFilters.push(sfEqual(key, value.toString().toUpperCase()).toString());
      }
    });

    const filtroMontado = sfAnd(itemFilters);

    return filtroMontado;
  };

  const onChangeFiltro = (campo: keyof IFiltrosGuiaRecolhimento, valor: string | number | Date) => {
    setFiltros((prevFiltros) => ({
      ...prevFiltros,
      [campo]: valor,
    }));
  };

  useEffect(() => {}, [filtros]);

  return (
    <div className={`${styles.filtrosAccordion} ${accordionHeaderStyle}`}>
      <button
        className={styles.accordionTitle}
        onClick={() => setIsFiltrosOpen(!isFiltrosOpen)}
        onKeyDown={() => setIsFiltrosOpen(!isFiltrosOpen)}
      >
        <span className={styles.title}>
          <i className={`fas fa-filter`} /> Filtrar Guias de Recolhimento
        </span>
        <i className={`fas fa-angle-down ${styles.icon} ${isFiltrosOpen ? styles.iconOpen : ''}`} />
      </button>
      <div className={`${styles.accordionContent} ${isFiltrosOpen ? styles.openContent : styles.closedContent}`}>
        <form autoComplete='off'>
          <Row>
            <Col>
              <AnmSelectQuery
                label='Nº do Processo'
                value={filtros?.['processo.numero']}
                keyQuery={['getProcessosMinerarios']}
                functionQuery={() => getProcessosMinerarios(userCpfCnpj || '')}
                formatResult={(result: string[]) =>
                  result.map((processo: string) => ({
                    label: formatarNrProcesso(processo),
                    value: formatarNrProcesso(processo),
                  }))
                }
                setValue={(value: string) => onChangeFiltro('processo.numero', value)}
                defaultValue={FIRST_OPTION_LABEL}
              />
            </Col>
            <Col>
              <AnmSelectQuery
                label='Substância'
                value={filtros?.['substanciaCorrelacionada.nome']}
                keyQuery={['getSubstancias']}
                functionQuery={() => getSubstancias()}
                formatResult={(result: ISubstancia[]) =>
                  result.map((substanciaCorrelacionada: ISubstancia) => ({
                    label: substanciaCorrelacionada.nome,
                    value: substanciaCorrelacionada.nome,
                  }))
                }
                setValue={(value: string) => onChangeFiltro('substanciaCorrelacionada.nome', value)}
                defaultValue={FIRST_OPTION_LABEL}
              />
            </Col>
            <Col>
              <BrSelect
                key={'estadoUFSelect' + estadoUF}
                label='Estado (UF)'
                options={[{ label: 'Selecionar', value: '-' }, ...estadosUFOptions]}
                value={estadoUF}
                onChange={(value: string) => setEstadoUF(value)}
              />
            </Col>
            <Col>
              <AnmSelectQuery
                key={'municipioSelect' + estadoUF}
                label='Município'
                value={filtros?.['municipio.nome']}
                keyQuery={['getMunicipiosPorUf']}
                functionQuery={() =>
                  estadoUF && estadoUF !== DEFAULT_OPTION_VALUE ? getMunicipiosPorUf(estadoUF) : Promise.resolve([])
                }
                formatResult={(result: IMunicipio[]) =>
                  result.map((municipio: IMunicipio) => ({
                    label: municipio.nome,
                    value: municipio.nome,
                  }))
                }
                setValue={(value: string) => onChangeFiltro('municipio.nome', value)}
                isDisabled={!estadoUF || estadoUF === DEFAULT_OPTION_VALUE}
                defaultValue={FIRST_OPTION_LABEL}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <BrDateTimePicker
                label='Competência'
                name='competencia'
                dataMode='single'
                dataType='month'
                maxDate={MAX_DATE}
                value={filtros?.['competencia']}
                onChange={(e) => onChangeFiltro('competencia', e)}
              />
            </Col>
            <Col>
              <BrSelect
                key={'tipoDeclaranteSelect' + filtros?.['tipoDeclarante']}
                label='Emitente'
                options={[
                  { label: FIRST_OPTION_LABEL, value: DEFAULT_OPTION_VALUE },
                  ...Object.entries(ETipoDeclaracao).map(([key, value]) => ({
                    label: value,
                    value: key,
                  })),
                ]}
                value={filtros?.['tipoDeclarante']}
                onChange={(value: string) => onChangeFiltro('tipoDeclarante', value)}
              />
            </Col>
            <Col>
              <BrSelect
                key={'tipoRecolhimentoSelect' + filtros?.['tipoRecolhimento']}
                label='Tipo de Guia'
                options={[
                  { label: FIRST_OPTION_LABEL, value: DEFAULT_OPTION_VALUE },
                  ...Object.entries(ETipoRecolhimento).map(([key, value]) => ({
                    label: value,
                    value: key,
                  })),
                ]}
                value={filtros?.['tipoRecolhimento']}
                onChange={(value: string) => onChangeFiltro('tipoRecolhimento', value)}
              />
            </Col>
            <Col>
              <BrSelect
                key={'situacaoSelect' + filtros?.['status']}
                label='Situação'
                options={[
                  { label: FIRST_OPTION_LABEL, value: DEFAULT_OPTION_VALUE },
                  ...Object.values(TipoSituacaoGuiaDetalhe)
                    .filter((situacao) => situacao.apresentarOpcaoComoFiltro)
                    .map((situacao) => ({
                      label: situacao.label,
                      value: situacao.nome,
                    })),
                ]}
                value={filtros?.['status']}
                onChange={(value: string) => onChangeFiltro('status', value)}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <BrSelect
                key={'formaPagamentoSelect' + filtros?.['formaPagamento']}
                label='Forma de Pagamento'
                options={[
                  { label: FIRST_OPTION_LABEL, value: DEFAULT_OPTION_VALUE },
                  ...Object.entries(EFormaPagamento).map(([key, value]) => ({
                    label: value,
                    value: key,
                  })),
                ]}
                value={filtros?.['formaPagamento']}
                onChange={(value: string) => onChangeFiltro('formaPagamento', value)}
              />
            </Col>
            <Col>
              <BrDateTimePicker
                label='Vencimento Inicial'
                name='dataVencimento'
                value={filtros?.['dataVencimento']}
                onChange={(e) => onChangeFiltro('dataVencimento', e)}
              />
            </Col>
            <Col>
              <BrDateTimePicker
                label='Vencimento Final'
                name='dataVencimentoFinal'
                value={filtros?.dataVencimentoFinal}
                onChange={(e) => onChangeFiltro('dataVencimentoFinal', e)}
              />
            </Col>
            <Col></Col>
          </Row>
        </form>
        <div className={styles.buttonContainer}>
          <BrButton secondary onClick={limparFiltros}>
            Limpar todos filtros
          </BrButton>
          <BrButton primary onClick={aplicarFiltros}>
            Aplicar filtros
          </BrButton>
        </div>
      </div>
    </div>
  );
};

export default FiltrosLista;
